import { useState, useRef } from 'react';
import { Card, Typography, Tabs, Button, Upload, Table, message, Space, Progress, Row, Col } from 'antd';
import {
  DownloadOutlined,
  UploadOutlined,
  FileTextOutlined,
  DeleteOutlined,
  InboxOutlined,
  CarOutlined,
  UserOutlined,
  TeamOutlined,
  AlertOutlined
} from '@ant-design/icons';
import type { UploadProps } from 'antd';
import { useTranslation } from 'react-i18next';
import { supabase, phoneToAuthEmail, provisionAuthUser, usePortalPermissions } from '@ubus/shared';
import AdminSectionPage from '../components/AdminSectionPage';

const { Title, Text } = Typography;
const { Dragger } = Upload;

// ============================================
// CSV 解析工具
// ============================================

function parseCSV(csvText: string, invalidMessage: string): { headers: string[]; rows: string[][] } {
  const lines = csvText.split(/\r?\n/).filter(line => line.trim());
  if (lines.length < 2) {
    throw new Error(invalidMessage);
  }

  // 解析表头
  const headers = parseCSVLine(lines[0]);
  
  // 解析数据行
  const rows: string[][] = [];
  for (let i = 1; i < lines.length; i++) {
    const row = parseCSVLine(lines[i]);
    if (row.length > 0) {
      rows.push(row);
    }
  }

  return { headers, rows };
}

function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    
    if (inQuotes) {
      if (char === '"') {
        if (i + 1 < line.length && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        current += char;
      }
    } else {
      if (char === '"') {
        inQuotes = true;
      } else if (char === ',') {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
  }
  result.push(current.trim());

  return result;
}

function convertToCSV(headers: string[], rows: Record<string, any>[]): string {
  const headerLine = headers.join(',');
  const dataLines = rows.map(row =>
    headers.map(h => {
      const val = String(row[h] ?? '');
      // 如果值包含逗号、引号或换行，用引号包裹
      if (val.includes(',') || val.includes('"') || val.includes('\n')) {
        return `"${val.replace(/"/g, '""')}"`;
      }
      return val;
    }).join(',')
  );
  return [headerLine, ...dataLines].join('\n');
}

// ============================================
// Data Management Page
// ============================================

function DataManagement() {
  const { t } = useTranslation();
  const { hasPermission } = usePortalPermissions();
  const canImport = hasPermission('data_management.import');
  const canExport = hasPermission('data_management.export');
  const [activeTab, setActiveTab] = useState<string>('import');
  const [importData, setImportData] = useState<{ headers: string[]; rows: string[][] } | null>(null);
  const [importTarget, setImportTarget] = useState<'vehicles' | 'drivers' | 'organizations'>('vehicles');
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0, errors: 0 });
  const [importing, setImporting] = useState(false);

  const processFile = async (file: File) => {
    try {
      const text = await file.text();
      const parsed = parseCSV(text, t('dataManagement.csvInvalid'));
      setImportData(parsed);
      message.success(t('dataManagement.parsed', { count: parsed.rows.length, name: file.name }));
    } catch (err: any) {
      message.error(t('dataManagement.parseFailed', { message: err.message }));
    }
    return false; // Prevent upload
  };

  const handleImport = async () => {
    if (!importData || importData.rows.length === 0) {
      message.warning(t('dataManagement.noData'));
      return;
    }

    setImporting(true);
    setImportProgress({ current: 0, total: importData.rows.length, errors: 0 });

    let errors = 0;
    const headers = importData.headers;

    for (let i = 0; i < importData.rows.length; i++) {
      const row = importData.rows[i];
      const record: Record<string, any> = {};

      // 将行数据转换为记录对象
      headers.forEach((header, index) => {
        if (index < row.length) {
          let value: string | number | string[] = row[index];
          // 类型转换
          if (['capacity', 'year', 'passenger_count'].includes(header.toLowerCase())) {
            value = parseInt(value) || 0;
          } else if (header.toLowerCase().includes('emails')) {
            // notification_emails 是数组
            value = value.split(';').map((e: string) => e.trim()).filter(Boolean);
          }
          record[header] = value;
        }
      });

      try {
        const { data: inserted, error } = await supabase
          .from(importTarget)
          .insert([record])
          .select('*')
          .single();

        if (error) {
          errors++;
          console.error(`Row ${i + 2} error:`, error);
        } else if (importTarget === 'drivers' && inserted?.id && record.phone) {
          try {
            await provisionAuthUser({
              role: 'driver',
              email: phoneToAuthEmail(String(record.phone)),
              phone: String(record.phone),
              driverId: inserted.id,
            });
          } catch (authErr) {
            errors++;
            console.error(`Row ${i + 2} auth error:`, authErr);
          }
        } else if (importTarget === 'organizations' && record.contact_email) {
          try {
            await provisionAuthUser({
              role: 'client',
              email: String(record.contact_email),
            });
          } catch (authErr) {
            errors++;
            console.error(`Row ${i + 2} auth error:`, authErr);
          }
        }
      } catch (err) {
        errors++;
        console.error(`Row ${i + 2} error:`, err);
      }

      setImportProgress({ current: i + 1, total: importData.rows.length, errors });
    }

    setImporting(false);

    if (errors === 0) {
      message.success(t('dataManagement.imported', { count: importData.rows.length }));
    } else {
      message.warning(t('dataManagement.importedWithErrors', { count: errors }));
    }

    setImportData(null);
  };

  const handleExport = async (table: string) => {
    try {
      message.loading({ content: t('dataManagement.exporting', { table }), key: 'export' });
      
      const { data, error } = await supabase.from(table).select('*');

      if (error) throw error;
      if (!data || data.length === 0) {
        message.info({ content: t('dataManagement.noDataInTable', { table }), key: 'export' });
        return;
      }

      // 确定表头
      const headers = Object.keys(data[0])
        .filter(h => !['id', 'created_at', 'updated_at', 'created_by', 'auth_uid'].includes(h));

      const csv = convertToCSV(headers, data);

      // 下载文件
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ubus_${table}_export_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);

      message.success({ content: t('dataManagement.exported', { count: data.length, table }), key: 'export' });
    } catch (err: any) {
      message.error({ content: t('dataManagement.exportFailed', { message: err.message }), key: 'export' });
    }
  };

  // CSV 示例数据
  const sampleData: Record<string, string> = {
    vehicles: `fleet_number,model,capacity,year,status
UB-001,Mercedes-Benz Sprinter,57,2023,active
UB-002,Volvo B8R,55,2024,active
UB-003,Toyota Coaster,22,2022,maintenance`,
    drivers: `name,phone,license_number,license_expiry,wwcc_expiry,emergency_contact_name,emergency_contact_phone,status
John Smith,+61412345678,DL-12345,2026-12-31,Jane Smith,+61498765432,active
Sarah Lee,+61423456789,DL-67890,2025-06-30,Mike Lee,+61487654321,active`,
    organizations: `name,contact_person,contact_phone,contact_email,address
Sydney Grammar School,David Chen,+61411111111,d.chen@sgs.edu.au,123 College St Sydney
Eastern Suburbs Primary,Sarah Johnson,+61422222222,s.johnson@esps.edu.au,456 Oxford St Bondi`
  };

  return (
    <AdminSectionPage
      title={t('dataManagement.title')}
      subtitle={t('dataManagement.subtitle')}
      bare
    >
      <Tabs activeKey={activeTab} onChange={setActiveTab} items={[
        {
          key: 'import',
          label: <span><UploadOutlined /> {t('dataManagement.importCsv')}</span>,
          children: (
            <Card className="admin-section-card" bordered={false}>
              <div style={{ padding: 20 }}>
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                {/* 导入目标 */}
                <div>
                  <Text strong style={{ marginRight: 12 }}>{t('dataManagement.importTarget')}</Text>
                  <Tabs
                    activeKey={importTarget}
                    onChange={(k) => { setImportTarget(k as any); setImportData(null); }}
                    type="card"
                    size="small"
                    items={[
                      { key: 'vehicles', label: t('dataManagement.vehicles') },
                      { key: 'drivers', label: t('dataManagement.drivers') },
                      { key: 'organizations', label: t('dataManagement.organizations') }
                    ]}
                  />
                </div>

                {/* 文件上传 */}
                <div>
                  <Dragger
                    accept=".csv"
                    showUploadList={false}
                    beforeUpload={processFile}
                    disabled={importing || !canImport}
                  >
                    <p className="ant-upload-drag-icon">
                      <InboxOutlined />
                    </p>
                    <p className="ant-upload-text">{t('dataManagement.clickOrDrag')}</p>
                    <p className="ant-upload-hint">
                      {t('dataManagement.uploadHint')}
                    </p>
                  </Dragger>
                </div>

                {/* 预览 */}
                {importData && (
                  <>
                    <Card title={t('dataManagement.preview', { count: importData.rows.length })} size="small">
                      <div style={{ maxHeight: 300, overflow: 'auto' }}>
                        <Table
                          dataSource={importData.rows.map((row, idx) => {
                            const record: Record<string, any> = { _key: idx };
                            importData.headers.forEach((h, i) => { record[h] = row[i] || ''; });
                            return record;
                          })}
                          columns={importData.headers.map(h => ({ title: h, dataIndex: h, key: h, ellipsis: true }))}
                          rowKey="_key"
                          size="small"
                          scroll={{ x: 'max-content' }}
                          pagination={{ pageSize: 10 }}
                        />
                      </div>
                    </Card>

                    {/* 导入进度 */}
                    {importing && (
                      <Progress
                        percent={Math.round((importProgress.current / importProgress.total) * 100)}
                        format={() => `${importProgress.current}/${importProgress.total}`}
                        status={importProgress.errors > 0 ? 'exception' : 'active'}
                      />
                    )}

                    <Space>
                      <Button
                        type="primary"
                        icon={<UploadOutlined />}
                        onClick={handleImport}
                        loading={importing}
                        disabled={!canImport || importData.rows.length === 0}
                      >
                        {t('dataManagement.importRecords', { count: importData.rows.length })}
                      </Button>
                      <Button icon={<DeleteOutlined />} onClick={() => setImportData(null)} disabled={importing}>
                        {t('dataManagement.clear')}
                      </Button>
                    </Space>
                  </>
                )}

                {/* CSV 示例 */}
                <Card title={t('dataManagement.csvFormatExample')} size="small" type="inner" className="admin-inner-card">
                  <pre className="admin-csv-sample">
                    {sampleData[importTarget]}
                  </pre>
                </Card>
              </Space>
              </div>
            </Card>
          )
        },
        {
          key: 'export',
          label: <span><DownloadOutlined /> {t('dataManagement.exportCsv')}</span>,
          children: (
            <Row gutter={[16, 16]} className="admin-export-grid">
                {[
                  { table: 'vehicles', label: t('dataManagement.vehicles'), icon: <CarOutlined />, desc: t('dataManagement.exports.vehicles') },
                  { table: 'drivers', label: t('dataManagement.drivers'), icon: <UserOutlined />, desc: t('dataManagement.exports.drivers') },
                  { table: 'organizations', label: t('dataManagement.organizations'), icon: <TeamOutlined />, desc: t('dataManagement.exports.organizations') },
                  { table: 'trips', label: t('dataManagement.trips'), icon: <FileTextOutlined />, desc: t('dataManagement.exports.trips') },
                  { table: 'sos_events', label: t('dataManagement.sosEvents'), icon: <AlertOutlined />, desc: t('dataManagement.exports.sosEvents') },
                  { table: 'trip_feedback', label: t('dataManagement.feedback'), icon: <FileTextOutlined />, desc: t('dataManagement.exports.feedback') },
                  { table: 'vehicle_inspections', label: t('dataManagement.inspections'), icon: <UserOutlined />, desc: t('dataManagement.exports.inspections') },
                  { table: 'fuel_expenses', label: t('dataManagement.expenses'), icon: <FileTextOutlined />, desc: t('dataManagement.exports.expenses') }
                ].map(item => (
                  <Col xs={24} sm={12} md={8} key={item.table}>
                    <Card
                      hoverable={canExport}
                      className="admin-export-card"
                      onClick={canExport ? () => handleExport(item.table) : undefined}
                    >
                      <div className="admin-export-card__icon">
                        {item.icon}
                      </div>
                      <Title level={4} style={{ marginBottom: 8 }}>{item.label}</Title>
                      <Text type="secondary">{item.desc}</Text>
                      <br />
                      <Button
                        type="primary"
                        icon={<DownloadOutlined />}
                        style={{ marginTop: 12 }}
                        disabled={!canExport}
                      >
                        {t('dataManagement.export')}
                      </Button>
                    </Card>
                  </Col>
                ))}
            </Row>
          )
        }
      ]} />
    </AdminSectionPage>
  );
}

export default DataManagement;
